/**
 * Campaign Validation Utilities
 *
 * Three-tier validation system for Campaign Orders:
 * 1. Order-level: Total budget and date range constraints
 * 2. Campaign-level: Budget allocation and date range within order
 * 3. Line item-level: Budget and dates within parent campaign
 */

/**
 * Validates that the sum of campaign budgets does not exceed the order budget
 * Also warns if more than 20% of budget remains unallocated
 *
 * @param {Array} campaigns - Array of campaign objects with budget property
 * @param {number} orderBudget - Total order budget
 * @returns {Object} - { valid: boolean, error: string|null, warning: string|null }
 */
export const validateCampaignBudgets = (campaigns, orderBudget) => {
  if (!campaigns || campaigns.length === 0) {
    return {
      valid: false,
      error: 'At least one campaign is required',
      warning: null,
    };
  }

  const totalCampaignBudget = campaigns.reduce(
    (sum, campaign) => sum + (parseFloat(campaign.budget) || 0),
    0
  );

  // Check if campaign budgets exceed order budget
  if (totalCampaignBudget > orderBudget) {
    return {
      valid: false,
      error: `Total campaign budgets ($${totalCampaignBudget.toFixed(2)}) exceed order budget ($${orderBudget.toFixed(2)})`,
      warning: null,
    };
  }

  // Check budget utilization and warn if too much is unallocated
  const utilization = orderBudget > 0 ? (totalCampaignBudget / orderBudget) * 100 : 0;
  const unallocated = orderBudget - totalCampaignBudget;

  let warning = null;
  if (utilization < 80 && unallocated > 0) {
    warning = `${utilization.toFixed(0)}% of budget allocated. $${unallocated.toFixed(2)} remaining unallocated.`;
  }

  return {
    valid: true,
    error: null,
    warning,
    totalAllocated: totalCampaignBudget,
    utilization,
    unallocated,
  };
};

/**
 * Validates that campaign dates fall within order date range
 * Campaign dates are optional and inherit from order if not specified
 *
 * @param {Object} campaign - Campaign object with startDate and endDate (optional)
 * @param {Date|Moment} orderStartDate - Order start date
 * @param {Date|Moment} orderEndDate - Order end date
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export const validateCampaignDates = (campaign, orderStartDate, orderEndDate) => {
  // If campaign has no dates, it inherits from order (always valid)
  if (!campaign.startDate || !campaign.endDate) {
    return {
      valid: true,
      error: null,
      inherited: true,
    };
  }

  // Convert to Date objects for comparison if they're moment objects
  const campaignStart = campaign.startDate._isAMomentObject
    ? campaign.startDate.toDate()
    : new Date(campaign.startDate);

  const campaignEnd = campaign.endDate._isAMomentObject
    ? campaign.endDate.toDate()
    : new Date(campaign.endDate);

  const orderStart = orderStartDate._isAMomentObject
    ? orderStartDate.toDate()
    : new Date(orderStartDate);

  const orderEnd = orderEndDate._isAMomentObject
    ? orderEndDate.toDate()
    : new Date(orderEndDate);

  // Validate campaign start date
  if (campaignStart < orderStart) {
    return {
      valid: false,
      error: `Campaign start date (${campaignStart.toLocaleDateString()}) is before order start date (${orderStart.toLocaleDateString()})`,
      inherited: false,
    };
  }

  // Validate campaign end date
  if (campaignEnd > orderEnd) {
    return {
      valid: false,
      error: `Campaign end date (${campaignEnd.toLocaleDateString()}) is after order end date (${orderEnd.toLocaleDateString()})`,
      inherited: false,
    };
  }

  // Validate campaign date range
  if (campaignEnd < campaignStart) {
    return {
      valid: false,
      error: 'Campaign end date must be after start date',
      inherited: false,
    };
  }

  return {
    valid: true,
    error: null,
    inherited: false,
  };
};

/**
 * Validates that the sum of line item budgets does not exceed campaign budget
 *
 * @param {Array} lineItems - Array of line item objects with budgetTotal property
 * @param {number} campaignBudget - Campaign budget
 * @returns {Object} - { valid: boolean, error: string|null, warning: string|null }
 */
export const validateLineItemBudgets = (lineItems, campaignBudget) => {
  if (!lineItems || lineItems.length === 0) {
    return {
      valid: false,
      error: 'At least one line item is required',
      warning: null,
    };
  }

  const totalLineItemBudget = lineItems.reduce(
    (sum, item) => sum + (parseFloat(item.budgetTotal) || 0),
    0
  );

  if (totalLineItemBudget > campaignBudget) {
    return {
      valid: false,
      error: `Line item budgets ($${totalLineItemBudget.toFixed(2)}) exceed campaign budget ($${campaignBudget.toFixed(2)})`,
      warning: null,
    };
  }

  // Check utilization
  const utilization = campaignBudget > 0 ? (totalLineItemBudget / campaignBudget) * 100 : 0;
  const unallocated = campaignBudget - totalLineItemBudget;

  let warning = null;
  if (utilization < 80 && unallocated > 0) {
    warning = `${utilization.toFixed(0)}% of campaign budget allocated. $${unallocated.toFixed(2)} remaining.`;
  }

  return {
    valid: true,
    error: null,
    warning,
    totalAllocated: totalLineItemBudget,
    utilization,
    unallocated,
  };
};

/**
 * Validates line item dates against campaign or order dates
 *
 * @param {Object} lineItem - Line item with startDate and endDate
 * @param {Date|Moment} campaignStartDate - Campaign start date (or order start if no campaign dates)
 * @param {Date|Moment} campaignEndDate - Campaign end date (or order end if no campaign dates)
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export const validateLineItemDates = (lineItem, campaignStartDate, campaignEndDate) => {
  if (!lineItem.startDate || !lineItem.endDate) {
    return {
      valid: false,
      error: 'Line item start and end dates are required',
    };
  }

  // Convert to Date objects
  const lineItemStart = lineItem.startDate._isAMomentObject
    ? lineItem.startDate.toDate()
    : new Date(lineItem.startDate);

  const lineItemEnd = lineItem.endDate._isAMomentObject
    ? lineItem.endDate.toDate()
    : new Date(lineItem.endDate);

  const campaignStart = campaignStartDate._isAMomentObject
    ? campaignStartDate.toDate()
    : new Date(campaignStartDate);

  const campaignEnd = campaignEndDate._isAMomentObject
    ? campaignEndDate.toDate()
    : new Date(campaignEndDate);

  // Validate line item dates against campaign dates
  if (lineItemStart < campaignStart) {
    return {
      valid: false,
      error: `Line item start date (${lineItemStart.toLocaleDateString()}) is before campaign start date (${campaignStart.toLocaleDateString()})`,
    };
  }

  if (lineItemEnd > campaignEnd) {
    return {
      valid: false,
      error: `Line item end date (${lineItemEnd.toLocaleDateString()}) is after campaign end date (${campaignEnd.toLocaleDateString()})`,
    };
  }

  if (lineItemEnd < lineItemStart) {
    return {
      valid: false,
      error: 'Line item end date must be after start date',
    };
  }

  return {
    valid: true,
    error: null,
  };
};

/**
 * Comprehensive campaign validation that checks all tiers
 * Returns an object with all validation results
 *
 * @param {Object} formValues - Complete form values with order and campaign data
 * @returns {Object} - Comprehensive validation results
 */
export const validateAllCampaigns = (formValues) => {
  const {
    budget: orderBudget,
    startDate: orderStartDate,
    endDate: orderEndDate,
    campaigns = [],
  } = formValues;

  // Order-level validation
  const budgetValidation = validateCampaignBudgets(campaigns, orderBudget);

  // Campaign-level validations
  const campaignValidations = campaigns.map((campaign, index) => {
    const dateValidation = validateCampaignDates(campaign, orderStartDate, orderEndDate);

    // Use campaign dates if available, otherwise use order dates
    const effectiveStartDate = campaign.startDate || orderStartDate;
    const effectiveEndDate = campaign.endDate || orderEndDate;

    const lineItemBudgetValidation = validateLineItemBudgets(
      campaign.lineItems || [],
      campaign.budget || 0
    );

    // Line item date validations
    const lineItemDateValidations = (campaign.lineItems || []).map(lineItem =>
      validateLineItemDates(lineItem, effectiveStartDate, effectiveEndDate)
    );

    return {
      campaignIndex: index,
      campaignName: campaign.name || `Campaign ${index + 1}`,
      dateValidation,
      lineItemBudgetValidation,
      lineItemDateValidations,
      isValid: dateValidation.valid &&
               lineItemBudgetValidation.valid &&
               lineItemDateValidations.every(v => v.valid),
    };
  });

  // Overall validation
  const isValid = budgetValidation.valid &&
                 campaignValidations.every(v => v.isValid);

  return {
    isValid,
    budgetValidation,
    campaignValidations,
    errors: [
      budgetValidation.error,
      ...campaignValidations.flatMap(v => [
        v.dateValidation.error,
        v.lineItemBudgetValidation.error,
        ...v.lineItemDateValidations.map(lv => lv.error),
      ]),
    ].filter(Boolean),
    warnings: [
      budgetValidation.warning,
      ...campaignValidations.map(v => v.lineItemBudgetValidation.warning),
    ].filter(Boolean),
  };
};

/**
 * Gets campaign count constraints
 * @returns {Object} - { min: number, max: number }
 */
export const getCampaignCountConstraints = () => ({
  min: 1,
  max: 10,
});

/**
 * Validates campaign count
 * @param {number} count - Number of campaigns
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export const validateCampaignCount = (count) => {
  const constraints = getCampaignCountConstraints();

  if (count < constraints.min) {
    return {
      valid: false,
      error: `At least ${constraints.min} campaign is required`,
    };
  }

  if (count > constraints.max) {
    return {
      valid: false,
      error: `Maximum ${constraints.max} campaigns allowed per order`,
    };
  }

  return {
    valid: true,
    error: null,
  };
};

/**
 * Validates if budget changes are allowed for flight schedules
 *
 * Nexxen restricts budget changes for flights starting within 1 hour of current time.
 * This validation prevents users from submitting changes that would be rejected by the API.
 *
 * @param {Array} newFlightSchedules - New flight schedules with startDate and budget
 * @param {Array} existingFlightSchedules - Existing flight schedules (from server) to compare against
 * @returns {Object} - { valid: boolean, error: string|null, blockedFlights: Array }
 */
export const validateBudgetUpdateAllowed = (newFlightSchedules, existingFlightSchedules) => {
  if (!newFlightSchedules || newFlightSchedules.length === 0) {
    return { valid: true, error: null, blockedFlights: [] };
  }

  if (!existingFlightSchedules || existingFlightSchedules.length === 0) {
    // No existing schedules means this is a new line item, no restrictions
    return { valid: true, error: null, blockedFlights: [] };
  }

  const now = new Date();
  const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
  // Budget comparison tolerance for floating-point precision (consistent with backend)
  const BUDGET_TOLERANCE = 0.01;
  const blockedFlights = [];

  for (const newFlight of newFlightSchedules) {
    if (!newFlight.startDate) continue;

    const flightStart = newFlight.startDate._isAMomentObject
      ? newFlight.startDate.toDate()
      : new Date(newFlight.startDate);

    // Only check flights starting within 1 hour
    if (flightStart > oneHourFromNow) continue;

    // Find matching existing flight by start date (within same day)
    const existingFlight = existingFlightSchedules.find(existing => {
      const existingStart = existing.startDate._isAMomentObject
        ? existing.startDate.toDate()
        : new Date(existing.startDate);

      // Match schedules by start date (within same day)
      return Math.abs(existingStart.getTime() - flightStart.getTime()) < 24 * 60 * 60 * 1000;
    });

    if (!existingFlight) continue;

    // Compare budgets with tolerance for floating-point precision
    const newBudget = parseFloat(newFlight.budget) || 0;
    const existingBudget = parseFloat(existingFlight.budget) || 0;

    if (Math.abs(newBudget - existingBudget) > BUDGET_TOLERANCE) {
      blockedFlights.push({
        startDate: flightStart,
        newBudget,
        existingBudget,
      });
    }
  }

  if (blockedFlights.length > 0) {
    const flightTimes = blockedFlights
      .map(f => f.startDate.toLocaleString('en-US', { timeZone: 'America/New_York' }))
      .join(', ');

    return {
      valid: false,
      error: `Cannot modify budget for flight(s) starting at ${flightTimes} (ET). ` +
             `Nexxen requires budget changes to be made more than 1 hour before the flight start time.`,
      blockedFlights,
    };
  }

  return { valid: true, error: null, blockedFlights: [] };
};

/**
 * Checks if a flight schedule is within the budget change restriction window (starting within 1 hour)
 *
 * @param {Object} flight - Flight schedule with startDate
 * @returns {boolean} - true if the flight is starting within 1 hour (budget changes restricted)
 */
export const isFlightBudgetRestricted = (flight) => {
  if (!flight || !flight.startDate) return false;

  const now = new Date();
  const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);

  const flightStart = flight.startDate._isAMomentObject
    ? flight.startDate.toDate()
    : new Date(flight.startDate);

  return flightStart <= oneHourFromNow;
};

/**
 * Detects overlapping date ranges in flight schedules
 *
 * @param {Array} flightSchedules - Array of flight schedule objects with startDate and endDate
 * @returns {Array} - Array of overlap objects { flightIndex1, flightIndex2 } indicating which flights overlap
 */
export const detectFlightOverlaps = (flightSchedules) => {
  if (!flightSchedules || flightSchedules.length <= 1) {
    return [];
  }

  const overlaps = [];
  for (let i = 0; i < flightSchedules.length; i++) {
    for (let j = i + 1; j < flightSchedules.length; j++) {
      const flight1 = flightSchedules[i];
      const flight2 = flightSchedules[j];

      // Skip if either flight is missing dates
      if (!flight1.startDate || !flight1.endDate || !flight2.startDate || !flight2.endDate) {
        continue;
      }

      const start1 = flight1.startDate._isAMomentObject
        ? flight1.startDate.toDate()
        : new Date(flight1.startDate);
      const end1 = flight1.endDate._isAMomentObject
        ? flight1.endDate.toDate()
        : new Date(flight1.endDate);
      const start2 = flight2.startDate._isAMomentObject
        ? flight2.startDate.toDate()
        : new Date(flight2.startDate);
      const end2 = flight2.endDate._isAMomentObject
        ? flight2.endDate.toDate()
        : new Date(flight2.endDate);

      // Check for overlap: ranges overlap if start1 <= end2 AND end1 >= start2
      if (start1 <= end2 && end1 >= start2) {
        overlaps.push({ flightIndex1: i, flightIndex2: j });
      }
    }
  }

  return overlaps;
};
